<template>
  <ak-button
    ref="button"
    :type="type"
    :tag="tag"
    circle
    :to="to"
    :size="size"
    class="ak-fab"
    :class="fabClasses"
    :disabled="disabled"
    @click="clicked"
  >
    <ak-icon :type="icon" :size="iconSize" />
  </ak-button>
</template>

<script>
import { ref, computed, defineComponent } from 'vue'
import { colors, nativeTypes, types, sizes } from '../button/Button.config'
import { sizes as iconSizes } from '../icon/Icon.config'
import Button from '../button/Button.vue'
import Icon from '../icon/Icon.vue'
import LoadingSpinner from '../loading/LoadingSpinner.vue'
import { buttonMixin } from '../../mixins/buttonMixin'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-button :ghost=false :type="'secondary'">Click Me</ak-button>
   */

  name: 'AkFab',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [LoadingSpinner.name]: LoadingSpinner,
    [Button.name]: Button,
    [Icon.name]: Icon,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {String}
     * @description This controls what icon is displayed
     */
    icon: {
      default: 'plus',
      type: String,
    },

    /**
     * @type {Boolean}
     * @description This controls whether we render the as a disabled
     * button or not, this will prevent click handlers from triggering
     * if using the native button element class.
     */

    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Object}
     * @prop {Function} validator - Ensures a valid type is used
     * @prop {String} type - The type of the property
     * @prop {String} default - The default value of the property
     * @description Controls the type of the button component that will
     * be rendered i.e submit/reset/button
     */
    type: {
      validator: (v) => nativeTypes.indexOf(v) >= 0,
      type: String,
      default: 'button',
    },

    /**
     * @type {Object}
     * @prop {Function} validator - Ensures a valid type is used
     * @prop {String} type - The type of the property
     * @prop {String} default - The default value of the property
     * @description Controls the element type of the button component that
     * will be rendered
     */
    tag: {
      validator: (v) => types.indexOf(v) >= 0,
      type: String,
      default: 'button',
    },

    /**
     * @type {String|Object}
     * @description The to prop required for router-link components
     */
    to: {
      type: [String, Object],
      default: undefined,
    },

    /**
     * @type {Object}
     * @prop {Function} validator - Ensures a valid type is used
     * @prop {String} type - The type of the property
     * @description Controls the size of the button component that will
     * be rendered
     */
    size: {
      validator: (v) => sizes.indexOf(v) >= 0,
      type: String,
      default: 'large',
    },

    /**
     * @type {Object}
     * @prop {Function} validator - Ensures a valid type is used
     * @prop {String} type - The type of the property
     * @description Controls the size of the button component that will
     * be rendered
     */
    iconSize: {
      validator: (v) => iconSizes.indexOf(v) >= 0,
      type: String,
      default: 'big',
    },

    /**
     * @type {Object}
     * @prop {Function} validator - Ensures a valid type is used
     * @prop {String} type - The type of the property
     * @prop {String} default - The default value of the property
     * @description Controls the color of the button component that will
     * be rendered
     */
    color: {
      validator: (v) => colors.indexOf(v) >= 0,
      type: String,
      default: 'primary',
    },

    /**
     * @type {Object}
     * @prop {Function} validator - Ensures a valid type is used
     * @prop {String} type - The type of the property
     * @prop {String} default - The default value of the property
     * @description Controls the color of the button component that will
     * be rendered
     */
    positionX: {
      validator: (v) => ['left', 'center', 'right'].indexOf(v) >= 0,
      type: String,
      default: null,
    },

    /**
     * @type {Object}
     * @prop {Function} validator - Ensures a valid type is used
     * @prop {String} type - The type of the property
     * @prop {String} default - The default value of the property
     * @description Controls the color of the button component that will
     * be rendered
     */
    positionY: {
      validator: (v) => ['top', 'middle', 'bottom'].indexOf(v) >= 0,
      type: String,
      default: null,
    },

    /**
     * @type {Boolean}
     * @prop {String} type - The type of the property
     * @prop {String} default - The default value of the property
     * @description Controls whether to display a spinner whilst loading
     */
    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  /*
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['click'],

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const button = ref(null)

    const fabClasses = computed(() => {
      const prefix = 'ak-fab'
      return {
        [`${prefix}--positioned`]: props.positionY || props.positionX,
        [`${prefix}--position-${props.positionY}`]: props.positionY,
        [`${prefix}--position-${props.positionX}`]: props.positionX,
      }
    })

    const { clicked, focus } = buttonMixin({
      button,
      props,
      emit,
    })

    return {
      button,
      fabClasses,
      focus,
      clicked,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
@import '../../scss/_mixins/_custom';

.ak-fab {
  box-shadow: $base-shadow-dark;

  &:active {
    transform: scale(0.9);
  }
  &--positioned {
    position: absolute;
    margin: $spacing;
  }
  &--position {
    $position: &;
    &-left {
      left: 0;
    }
    &-right {
      right: 0;
    }
    &-center {
      left: 50%;
      transform: translateX(-50%);
      &:active {
        transform: translateX(-50%) scale(0.9);
      }
    }
    &-top {
      top: 0;
    }
    &-bottom {
      bottom: 0;
    }
    &-middle {
      top: 50%;
      transform: translateY(-50%);
      &:active {
        transform: translateY(-50%) scale(0.9);
      }
    }
  }
}
</style>
